import type { FaqItem } from './BlogPostLayout';

/** Violet-bordered TL;DR callout at the top of a post. AI-Overview bait. */
export function TldrCallout({ children }: { children: React.ReactNode }) {
  return (
    <aside className="p-6 rounded-xl border border-border bg-card mb-10">
      <h2 className="text-sm font-bold text-primary tracking-[0.12em] uppercase mb-3">TL;DR</h2>
      <div className="text-muted-foreground text-sm leading-[1.7] space-y-3">{children}</div>
    </aside>
  );
}

/** Table of contents nav. Smooth-scrolls to each section on click. */
export function Toc({ entries }: { entries: Array<{ id: string; label: string }> }) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Update the URL hash so the link is copyable and back-button friendly,
    // without triggering the browser's default instant jump.
    history.replaceState(null, '', `#${id}`);
  };

  return (
    <nav aria-label="Table of contents" className="p-6 rounded-xl border border-border bg-card mb-12">
      <h2 className="text-sm font-bold text-foreground tracking-wide uppercase mb-4">
        Table of Contents
      </h2>
      <ol className="space-y-2 text-sm">
        {entries.map((entry) => (
          <li key={entry.id}>
            <a
              href={`#${entry.id}`}
              onClick={(e) => handleClick(e, entry.id)}
              className="text-muted-foreground hover:text-primary transition-colors cursor-pointer"
            >
              {entry.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * Card-styled FAQ section with proper microdata. Pass the same `items` array
 * to BlogPostLayout's `faq` prop so JSON-LD stays in sync.
 */
export function FaqSection({ items }: { items: FaqItem[] }) {
  return (
    <section id="faq" itemScope itemType="https://schema.org/FAQPage">
      <div className="text-center mb-10 mt-12">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-[#a78bfa] text-xs font-semibold tracking-wider uppercase mb-4">
          💬 Common Questions
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
          Frequently asked questions
        </h2>
      </div>

      <div className="space-y-4">
        {items.map((item, i) => (
          <div
            key={item.question}
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
            className="group rounded-xl border border-border bg-card p-6 hover:border-primary/35 transition-colors"
          >
            <div className="flex items-start gap-4">
              <span
                aria-hidden="true"
                className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/15 text-primary text-sm font-bold border border-primary/30"
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              <div className="flex-1 min-w-0">
                <h3
                  itemProp="name"
                  className="text-base sm:text-lg font-semibold text-foreground leading-snug mb-3 group-hover:text-primary transition-colors"
                >
                  {item.question}
                </h3>
                <div
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                  className="pl-4 border-l-2 border-primary/20"
                >
                  <p itemProp="text" className="text-muted-foreground text-[15px] leading-[1.7]">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * In-body CTA. Designed to feel like a tip-callout woven into the prose,
 * not a sales banner. Use mid-article at a moment of natural reader pain.
 */
export function InlineCta({
  eyebrow,
  heading,
  body,
  buttonLabel,
  buttonHref,
}: {
  eyebrow?: string;
  heading: string;
  body: string;
  buttonLabel: string;
  buttonHref: string;
}) {
  return (
    <aside
      className="my-10 p-6 rounded-xl border-l-4 border-primary border border-border bg-primary/[0.04]"
      aria-label="Try StoryMint"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex-1 min-w-0">
          {eyebrow && (
            <div className="text-[10px] font-bold text-primary tracking-[0.12em] uppercase mb-1.5">
              {eyebrow}
            </div>
          )}
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-2 leading-snug">
            {heading}
          </h3>
          <p className="text-sm text-muted-foreground leading-[1.65]">{body}</p>
        </div>
        <a
          href={buttonHref}
          className="shrink-0 inline-flex items-center justify-center px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-md hover:bg-primary/90 transition-colors whitespace-nowrap"
        >
          {buttonLabel}
        </a>
      </div>
    </aside>
  );
}

/** Bottom-of-post conversion card. */
export function CtaCard({
  heading,
  body,
  buttonLabel,
  buttonHref,
}: {
  heading: string;
  body: string;
  buttonLabel: string;
  buttonHref: string;
}) {
  return (
    <section className="mt-16 p-8 rounded-xl border border-primary bg-primary/5 text-center">
      <h2 className="text-2xl font-bold text-foreground mb-3">{heading}</h2>
      <p className="text-muted-foreground mb-6">{body}</p>
      <a
        href={buttonHref}
        className="inline-block px-8 py-3 bg-primary text-primary-foreground font-semibold rounded-md hover:bg-primary/90 transition-colors text-lg"
      >
        {buttonLabel}
      </a>
    </section>
  );
}

/**
 * Inline illustration to break up long body sections. Slightly slimmer than
 * the post hero so it complements rather than competes. Lazy-loaded.
 */
export function InlineImage({
  src,
  alt,
  caption,
}: {
  src: string;
  alt: string;
  caption?: string;
}) {
  return (
    <figure className="my-8">
      <div
        className="rounded-xl border border-border overflow-hidden h-56 sm:h-72 relative"
        style={{ background: 'linear-gradient(135deg, #0d0d12 0%, #1a1a2e 100%)' }}
      >
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
      {caption && (
        <figcaption className="mt-3 text-xs text-muted-foreground text-center italic">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/** Headed section anchored by id - keeps post body uniform. */
export function PostSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} style={{ scrollMarginTop: '24px' }}>
      <h2 className="text-2xl sm:text-3xl font-bold text-foreground mt-12 mb-4 tracking-tight">
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
